import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { TextArea, type TextAreaProps } from '../primitives/TextArea';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { Reaction } from './reactions';
import { useReactions } from './useReactions';
import type { RhfLayoutProps } from './rhfLayout';

export interface RhfTextAreaProps
  extends Omit<TextAreaProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'error'>, RhfLayoutProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  formatter?: (value: string) => string;
  reactions?: Reaction[];
}

export const RhfTextArea = React.forwardRef<HTMLTextAreaElement, RhfTextAreaProps>(
  function RhfTextArea(
    { control, name, label, hint, required = false, semanticId, disabled, id, formatter, controlWidth, reactions, ...rest },
    ref,
  ) {
    const controlId = id ?? name;
    const overrides = useReactions(control, reactions);

    const finalDisabled = overrides.disabled ?? disabled ?? false;
    const finalRequired = (overrides.required as boolean) ?? required;
    const finalLabel = (overrides.label as string) ?? label;

    if (overrides.visible === false) return null;

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          return (
            <FormField
              label={finalLabel}
              htmlFor={controlId}
              hint={hint}
              required={finalRequired}
              semanticId={semanticId}
              disabled={finalDisabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <TextArea
                ref={composeRefs(field.ref, ref)}
                id={controlId}
                name={field.name}
                value={field.value ?? ''}
                onChange={
                  formatter
                    ? (e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        field.onChange(formatter(e.target.value))
                    : field.onChange
                }
                onBlur={field.onBlur}
                disabled={finalDisabled}
                error={Boolean(errorText)}
                semanticId={semanticId}
                {...rest}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfTextArea.displayName = 'RhfTextArea';
